import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import {
  getArticleById,
  getRelatedArticles,
  hotTopics,
  getCategoryBySlug,
} from "@/mocks/articles";
import SchemaOrg from "@/components/base/SchemaOrg";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || "");
  const [copied, setCopied] = useState(false);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = getRelatedArticles(
    article.id,
    article.categorySlug,
    4
  );
  const category = getCategoryBySlug(article.categorySlug);

  const breadcrumbItems = [
    { name: "首页", url: "http://biomicskin.cn/" },
    {
      name: article.category,
      url: `http://biomicskin.cn/category/${article.categorySlug}`,
    },
    { name: "文章详情", url: `http://biomicskin.cn/article/${article.id}` },
  ];

  const handleShare = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <>
      <SchemaOrg
        type="Article"
        title={`${article.title} - 吃瓜网51`}
        description={article.summary}
        article={{
          headline: article.title,
          description: article.summary,
          image: article.image,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          author: article.author,
          category: article.category,
          tags: article.tags,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbs={breadcrumbItems}
      />

      <div className="w-full px-4 md:px-8 py-6 md:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <article className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
            {/* Cover Image */}
            <div className="relative aspect-[16/9] w-full overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-colors"
                  >
                    {article.category}
                  </Link>
                  {article.hotTag && (
                    <span className="px-2 py-0.5 rounded bg-accent-500 text-background-50 text-xs font-bold">
                      {article.hotTag}
                    </span>
                  )}
                </div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-background-50 leading-snug">
                  {article.title}
                </h1>
              </div>
            </div>

            {/* Meta Info */}
            <div className="px-5 md:px-8 py-4 border-b border-background-200/70 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                  {article.readCount > 10000
                    ? `${(article.readCount / 10000).toFixed(1)}万`
                    : article.readCount}
                  阅读
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors"
                >
                  <i className="ri-share-line w-3.5 h-3.5 flex items-center justify-center" />
                  {copied ? "已复制" : "分享"}
                </button>
                <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors">
                  <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center" />
                  收藏
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 md:px-8 py-6 md:py-8">
              {/* Summary Box */}
              <div className="p-4 md:p-5 rounded-xl bg-accent-50 border border-accent-200/70 mb-6 md:mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line w-5 h-5 flex items-center justify-center text-accent-600 text-lg shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {article.summary}
                  </p>
                </div>
              </div>

              {/* Article Body */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200/70">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-8 md:mt-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-links-line w-5 h-5 flex items-center justify-center text-primary-500" />
                  相关推荐
                </h2>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                  >
                    查看更多
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="flex gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:shadow-md transition-all group"
                  >
                    <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-background-100">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-center min-w-0">
                      <span className="text-xs text-primary-600 font-medium mb-1.5">
                        {rel.category}
                      </span>
                      <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {rel.title}
                      </h3>
                      <span className="text-xs text-foreground-400 mt-2">
                        {rel.publishDate}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Hot Topics Sidebar */}
          <div className="mt-8 md:mt-10 p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/70">
            <h3 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line w-5 h-5 flex items-center justify-center text-primary-500" />
              全网热搜
            </h3>
            <div className="space-y-3">
              {hotTopics.slice(0, 8).map((topic, index) => (
                <Link
                  key={topic.id}
                  to={topic.link}
                  className="flex items-center gap-3 group"
                >
                  <span
                    className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                      index < 3
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors truncate">
                    {topic.title}
                  </span>
                  <span className="text-xs text-foreground-400 shrink-0 ml-auto">
                    {(topic.heat / 10000).toFixed(0)}万
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}